import dayjs from 'dayjs';
import type {
  AssistantChartResponse,
  AssistantTableResponse,
} from 'src/api/ProactApiClient';
import {
  AssistantChartUnitEnum,
  AssistantTableColumnFormatEnum,
} from 'src/api/ProactApiClient';
import {chartValueAt} from 'src/app/assistant/Chart/chatChartFormat';
import type {ChatTableRow} from 'src/app/assistant/Table/chatTableFormat';
import {
  isDateTimeCell,
  isNumericTableFormat,
  tableFormatUnit,
} from 'src/app/assistant/Table/chatTableFormat';
import {parseUtcDate} from 'src/shared/utils/parseUtcDate';

type CsvCellValue = string | number | null | undefined;

export const CSV_MIME_TYPE = 'text/csv;charset=utf-8';

const BOM = '﻿';
const LINE_END = '\r\n';
const FORMULA_TRIGGERS = new Set(['=', '+', '-', '@', '\t', '\r']);
const QUOTE_TRIGGER = /[",\r\n]/;

const quote = (value: string): string =>
  QUOTE_TRIGGER.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

// A spreadsheet evaluates a text cell that starts with a formula character, so it gets a
// leading '. A finite number cannot be a formula and stays unguarded so negatives stay numbers.
const formatCell = (value: CsvCellValue): string => {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  const text = value === null || value === undefined ? '' : String(value);
  if (!text) return '';
  return quote(FORMULA_TRIGGERS.has(text[0]) ? `'${text}` : text);
};

/**
 * RFC 4180 CSV with a UTF-8 byte order mark and CRLF line ends, matching the server's CSV exports.
 * @registry Builds a spreadsheet-safe CSV string (BOM, CRLF, quoting, formula guard on text cells).
 */
export const buildCsv = (
  header: readonly string[],
  rows: readonly (readonly CsvCellValue[])[]
): string =>
  BOM +
  [header, ...rows]
    .map(row => row.map(formatCell).join(',') + LINE_END)
    .join('');

// Raw numbers lose the $, % and h formatting, so the unit moves into the header.
const UNIT_HEADER_SUFFIX: Partial<Record<AssistantChartUnitEnum, string>> = {
  [AssistantChartUnitEnum.Currency]: 'USD',
  [AssistantChartUnitEnum.Percent]: '%',
  [AssistantChartUnitEnum.Hours]: 'hours',
};

const headerWithUnit = (
  label: string,
  unit: AssistantChartUnitEnum | null
): string => {
  const suffix = unit === null ? undefined : UNIT_HEADER_SUFFIX[unit];
  return suffix ? `${label} (${suffix})` : label;
};

/** A chart's "Show data" columns with raw numbers instead of formatted values. */
export const buildChartCsv = (chart: AssistantChartResponse): string => {
  const series = chart.series ?? [];
  return buildCsv(
    [chart.xLabel, ...series.map(s => headerWithUnit(s.label, chart.unit))],
    (chart.points ?? []).map(point => [
      point.x,
      ...series.map((_, index) => chartValueAt(point, index)),
    ])
  );
};

const toNumber = (value: string | null): CsvCellValue => {
  if (value === null || value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : value;
};

const toCsvDateTime = (value: string): string =>
  parseUtcDate(value).toISOString().slice(0, 19).replace('T', ' ');

/** A table's columns with the given rows (the shown rows, in display order) as raw values. */
export const buildTableCsv = (
  table: AssistantTableResponse,
  rows: readonly ChatTableRow[]
): string => {
  const columns = table.columns ?? [];
  const header = columns.map((column, index) => {
    const isDateTimeColumn =
      column.format === AssistantTableColumnFormatEnum.Date &&
      rows.some(row => isDateTimeCell(row[index] ?? null));
    return isDateTimeColumn
      ? `${column.label} (UTC)`
      : headerWithUnit(column.label, tableFormatUnit(column.format));
  });
  return buildCsv(
    header,
    rows.map(row =>
      columns.map((column, index): CsvCellValue => {
        const value = row[index] ?? null;
        if (isNumericTableFormat(column.format)) return toNumber(value);
        if (value !== null && isDateTimeCell(value))
          return toCsvDateTime(value);
        return value;
      })
    )
  );
};

/** Minimal hast shape: react-markdown hands table overrides the rendered element tree. */
export interface MarkdownTableNode {
  type: string;
  tagName?: string;
  value?: string;
  children?: readonly MarkdownTableNode[];
}

const PLAIN_NUMBER = /^-?\d+(\.\d+)?$/;

const nodeText = (node: MarkdownTableNode): string => {
  if (node.type === 'text') return node.value ?? '';
  if (node.tagName === 'br') return ' ';
  return (node.children ?? []).map(nodeText).join('');
};

const collectRows = (
  node: MarkdownTableNode,
  rows: MarkdownTableNode[] = []
): MarkdownTableNode[] => {
  if (node.tagName === 'tr') rows.push(node);
  else (node.children ?? []).forEach(child => collectRows(child, rows));
  return rows;
};

/** The cell text of a rendered markdown table, first row as the header. Null when it has no rows. */
export const buildMarkdownTableCsv = (
  table: MarkdownTableNode
): string | null => {
  const rows = collectRows(table).map(row =>
    (row.children ?? [])
      .filter(cell => cell.tagName === 'th' || cell.tagName === 'td')
      .map(cell => nodeText(cell).trim())
  );
  if (rows.length === 0) return null;
  const [header, ...body] = rows;
  return buildCsv(
    header,
    body.map(row =>
      row.map(cell => (PLAIN_NUMBER.test(cell) ? Number(cell) : cell))
    )
  );
};

type ScoutCsvKind = 'query' | 'chart' | 'table';

/** `scout-{kind}-YYYY-MM-DD-HHmmss.csv` in local time. */
export const scoutCsvFileName = (kind: ScoutCsvKind, at: Date): string =>
  `scout-${kind}-${dayjs(at).format('YYYY-MM-DD-HHmmss')}.csv`;
